import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React from 'react';
import { useIntl } from 'react-intl-next';
import { IconButton, LinkButton } from '@atlaskit/button/new';
import EmailIcon from '@atlaskit/icon/core/email';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import { Show } from '@atlaskit/primitives/responsive';
import TeamsAvatar from '@atlaskit/teams-avatar';
import { useAIAnswerContext } from '../ai-answer-dialog/ai-answer-context';
import { SainAction, useAnalytics, useSAINUsageCriteriaMet } from '../common/controller/analytics';
import { messages } from '../messages';
import { onKnowledgeCardLinkClick } from './analytics';
import { KnowledgeCardsLocationText, KnowledgeCardsPicture, KnowledgeCardsTimeText } from './styled';
import { KnowledgeCardsIntentType } from './types';
import { onEmailCopy } from './util';
var KnowledgeCardsHeaderWrapper = xcss({
  paddingTop: 'space.150',
  paddingRight: 'space.200',
  paddingBottom: 'space.150',
  paddingLeft: 'space.200'
});
var KnowledgeCardsPrimaryInfoWrapper = xcss({
  display: 'flex',
  width: '100%',
  height: '100%',
  flexDirection: 'column',
  justifyContent: 'center'
});
var KnowledgeCardsName = xcss({
  font: 'font.heading.medium',
  fontWeight: 'font.weight.medium'
});
var KnowledgeCardsSubText = xcss({
  font: 'font.body.small',
  color: 'color.text.subtlest'
});
var MutedText = xcss({
  color: 'color.text.subtle'
});
export var KnowledgeCardsHeader = function KnowledgeCardsHeader(_ref) {
  var type = _ref.type,
    name = _ref.name,
    email = _ref.email,
    avatarUrl = _ref.avatarUrl,
    profileUrl = _ref.profileUrl,
    jobTitle = _ref.jobTitle,
    department = _ref.department,
    location = _ref.location,
    time = _ref.time;
  var _useAIAnswerContext = useAIAnswerContext(),
    commonAttributes = _useAIAnswerContext.commonAttributes;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var _useAnalytics = useAnalytics(),
    _useAnalytics2 = _slicedToArray(_useAnalytics, 1),
    fireAnalyticsEvent = _useAnalytics2[0];
  var _useSAINUsageCriteria = useSAINUsageCriteriaMet(),
    fireSAINUsageEvent = _useSAINUsageCriteria.fireSAINUsageEvent;
  var isPerson = type === KnowledgeCardsIntentType.PERSON;
  var subText = isPerson ? [jobTitle, department].filter(function (text) {
    return !!text;
  }).join(', ') : formatMessage(messages.knowledge_cards_team_subheader);
  var trimmedSubText = subText.length > 250 ? "".concat(subText.slice(0, 250), "...") : subText;
  var viewText = isPerson ? messages.ai_answer_who_questions_view_profile : messages.knowledge_cards_view_team;
  var timeAndLocationComponent = isPerson && /*#__PURE__*/React.createElement(Inline, {
    space: "space.100"
  }, /*#__PURE__*/React.createElement(KnowledgeCardsTimeText, null, time), /*#__PURE__*/React.createElement(KnowledgeCardsLocationText, null, location));
  var tooltipOptions = {
    position: 'bottom',
    content: formatMessage(messages.knowledge_cards_copy_email_address),
    hideTooltipOnClick: true
  };
  return /*#__PURE__*/React.createElement(Inline, {
    alignBlock: "center",
    space: "space.100",
    xcss: KnowledgeCardsHeaderWrapper
  }, /*#__PURE__*/React.createElement(Inline, {
    spread: "space-between",
    alignInline: "start",
    alignBlock: "center",
    grow: "fill"
  }, /*#__PURE__*/React.createElement(Inline, {
    alignInline: "start",
    alignBlock: "center"
  }, !isPerson && fg('team-avatar-search-card') ? /*#__PURE__*/React.createElement(TeamsAvatar, {
    size: "xlarge",
    src: avatarUrl || undefined,
    name: name
  }) : /*#__PURE__*/React.createElement(KnowledgeCardsPicture, {
    src: avatarUrl || '',
    alt: name
  }), /*#__PURE__*/React.createElement(Box, {
    paddingInlineStart: "space.100",
    xcss: KnowledgeCardsPrimaryInfoWrapper
  }, /*#__PURE__*/React.createElement(Stack, {
    space: "space.075"
  }, /*#__PURE__*/React.createElement(Stack, null, /*#__PURE__*/React.createElement(Box, {
    xcss: KnowledgeCardsName
  }, name), /*#__PURE__*/React.createElement(Box, {
    xcss: KnowledgeCardsSubText
  }, trimmedSubText)), /*#__PURE__*/React.createElement(Show, {
    below: "sm"
  }, timeAndLocationComponent)))), /*#__PURE__*/React.createElement(Show, {
    above: "sm"
  }, timeAndLocationComponent)), /*#__PURE__*/React.createElement(Inline, {
    alignBlock: "center",
    alignInline: "end",
    space: "space.100"
  }, !!email && /*#__PURE__*/React.createElement(IconButton, {
    icon: function icon(props) {
      return /*#__PURE__*/React.createElement(EmailIcon, _extends({}, props, {
        color: "var(--ds-icon, #44546F)"
      }));
    },
    label: formatMessage(messages.ai_answer_who_questions_hover_email_icon),
    onClick: function onClick() {
      fireAnalyticsEvent(onKnowledgeCardLinkClick(commonAttributes, 'header-copy-email'));
      fireSAINUsageEvent({
        metCriteria: SainAction.COPY_CLICKED
      });
      onEmailCopy(email);
    },
    isTooltipDisabled: false,
    tooltip: tooltipOptions
  }), /*#__PURE__*/React.createElement(LinkButton, {
    href: profileUrl || '',
    onClick: function onClick() {
      fireAnalyticsEvent(onKnowledgeCardLinkClick(commonAttributes, 'header-view-profile'));
      fireSAINUsageEvent({
        metCriteria: SainAction.VIEW_PROFILE_CLICKED
      });
    }
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: MutedText
  }, formatMessage(viewText)))));
};